import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../lib/api';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    }
  }, [searchParams]);

  const verifyToken = async (tokenToVerify) => {
    setVerifying(true);
    try {
      const response = await authAPI.verifyResetToken(tokenToVerify);
      setTokenValid(true);
      setUserEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyToken = () => {
    if (!token.trim()) {
      toast.error('Please enter a reset token');
      return;
    }
    verifyToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      console.error('Reset password error:', error);
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">Password Reset!</h1>
              <p className="text-[#4A4A4A]">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
            </div>

            <Link to="/login">
              <Button className="btn-primary w-full" data-testid="go-to-login-btn">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-8 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        <div className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
          <div className="mb-8 text-center">
            <div className="w-16 h-16 bg-[#FFEDDF] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#1E3A5F]" />
            </div>
            <h1 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">Reset Password</h1>
            <p className="text-[#4A4A4A]">
              Enter your reset token and create a new password.
            </p>
          </div>

          {/* Token Input Section */}
          {tokenValid === null && (
            <div className="space-y-4 mb-6">
              <div className="space-y-2">
                <Label htmlFor="token" className="font-medium text-[#1E3A5F]">Reset Token</Label>
                <Input
                  id="token"
                  type="text"
                  placeholder="Enter your reset token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="input-default w-full font-mono"
                  data-testid="reset-token-input"
                />
              </div>
              <Button
                onClick={handleVerifyToken}
                className="btn-primary w-full"
                disabled={verifying}
                data-testid="verify-token-btn"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Token'
                )}
              </Button>
            </div>
          )}

          {/* Token Invalid */}
          {tokenValid === false && (
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-red-600 font-medium mb-4">Invalid or expired reset token</p>
              <Link to="/forgot-password">
                <Button variant="outline" className="border-2 border-[#1E3A5F]">
                  Request New Reset Token
                </Button>
              </Link>
            </div>
          )}

          {/* Token Valid - Show Password Form */}
          {tokenValid === true && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                  <p className="text-sm text-green-800">
                    Token verified for <strong>{userEmail}</strong>
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="font-medium text-[#1E3A5F]">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password (min 6 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="input-default w-full pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="font-medium text-[#1E3A5F]">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-default w-full pr-10"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <p className="text-[#4A4A4A]">
              Need a new token?{' '}
              <Link to="/forgot-password" className="text-[#FE5F55] font-semibold hover:underline">
                Request reset
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
